import { useState, useCallback } from "react";
import { ExpressCheckoutElement } from "@stripe/react-stripe-js";
import { Loader2Icon } from "lucide-react";
import type { StripeExpressCheckoutElementConfirmEvent } from "@stripe/stripe-js";
import { useAnalytics } from "@/hooks/useAnalytics";

interface GooglePayOnlyProps {
  onConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => Promise<void>;
  sessionId: string;
}

/**
 * GooglePayOnly - Dedicated Google Pay component for the googlepaybrowser flow
 * This component renders only the Google Pay button via ExpressCheckoutElement
 * with all other payment methods disabled.
 */
const GooglePayOnly = ({ onConfirm, sessionId }: GooglePayOnlyProps) => {
  const { track } = useAnalytics();
  const [isLoading, setIsLoading] = useState(true);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);

  const handleReady = useCallback((event: { availablePaymentMethods?: Record<string, boolean> }) => {
    setIsLoading(false);
    const availablePaymentMethods = event?.availablePaymentMethods;
    const hasGooglePay = !!(availablePaymentMethods?.googlePay);
    setHasPaymentMethod(hasGooglePay);
    if (!hasGooglePay) {
      setIsUnavailable(true);
    }
  }, []);

  const handleLoadError = useCallback((error: { elementType: string; error: { message?: string } }) => {
    console.error('GooglePayOnly ExpressCheckoutElement load error:', error);
    setIsLoading(false);
    setIsUnavailable(true);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center px-5 py-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            Pay with Google Pay
          </h2>
          <p className="text-sm text-gray-500">
            Complete your purchase securely with Google Pay
          </p>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2Icon className="animate-spin h-8 w-8 text-blue-500 mb-3" />
            <p className="text-sm text-gray-500">Loading Google Pay...</p>
          </div>
        )}

        {/* Unavailable state */}
        {isUnavailable && !isLoading && (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 w-full">
              <p className="text-amber-800 text-center text-sm font-medium mb-2">
                Google Pay is not available
              </p>
              <p className="text-amber-600 text-center text-xs">
                Google Pay is not available on this device or browser. 
                Please try again later.
              </p>
            </div>
          </div>
        )}

        {/* Google Pay button */}
        <div 
          className={`transition-opacity duration-300 ${isLoading ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'}`}
          style={{ minHeight: hasPaymentMethod ? '48px' : '0' }}
        >
          <ExpressCheckoutElement
            options={{
              emailRequired: true,
              paymentMethods: {
                applePay: "never",
                googlePay: "auto",
                paypal: "never",
                amazonPay: "never",
                link: "never",
                klarna: "never",
              },
              paymentMethodOrder: ["google_pay"],
              layout: {
                maxColumns: 1,
                overflow: "auto",
              },
            }}
            onClick={(event) => {
              try {
                track('click_in_checkout', { 
                  cta_name: 'pay', 
                  payment_method: event.expressPaymentType 
                });
              } catch {
                // Ignore tracking errors
              }
              event.resolve({});
            }}
            onConfirm={onConfirm}
            onReady={handleReady}
            onLoadError={handleLoadError}
          />
        </div>

        
      </div>
    </div>
  );
};

export default GooglePayOnly;


